/**
 * WaterPanel — reservoir storage for Chonburi province.
 * Data: RID's daily water report (app.rid.go.th), dated by the report itself.
 *
 * Critical framing for the mayor:
 *   "How long does the water last in the dam that is draining fastest?"
 *   → < 10 days: CRITICAL, < 30 days: LOW, < 120 days: WATCH, else OK
 *
 * The number is usable water above dead storage ÷ today's net depletion
 * (outflow − inflow). A dam that is filling has no exhaustion horizon, and
 * its row says so rather than printing a small number that looks urgent.
 */

import type { FallbackTier, ReservoirObservation } from "@chonburi/shared";
import { PanelHeader } from './PanelHeader';
import { alertLevel } from '../lib/water';

interface Props {
  data: ReservoirObservation[];
  loading: boolean;
  fallbackTier?: FallbackTier;
}

/**
 * The report's own date, formatted from the date string rather than from a
 * Date object — the report is dated in ICT, and a reader in UTC+13 would see
 * the wrong day if the string were parsed in their own timezone.
 */
function reportLabel(observedAt: string | undefined): string | null {
  if (!observedAt) return null;
  const [y, m, d] = observedAt.slice(0, 10).split("-");
  if (!y || !m || !d) return null;
  const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  const month = MONTHS[Number(m) - 1];
  if (!month) return null;
  return `${d} ${month} ${y}`;
}

const LEVEL_COLOR = {
  critical: "var(--bad)",
  low:      "var(--warn)",
  watch:    "var(--accent)",
  ok:       "var(--good)",
};

const TREND_ARROW: Record<ReservoirObservation["trend"], string> = {
  rising: "↑",
  falling: "↓",
  stable: "—",
  unknown: "?",
};

function Bar({ pct, level }: { pct: number | null; level: string }) {
  const w = Math.max(0, Math.min(100, pct ?? 0));
  const col = LEVEL_COLOR[level as keyof typeof LEVEL_COLOR] ?? "var(--data)";
  return (
    <div style={{ height: 4, background: "var(--line)", position: "relative", marginTop: 2 }}>
      <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${w}%`, background: col }} />
    </div>
  );
}

export function WaterPanel({ data, loading, fallbackTier }: Props) {
  if (loading && data.length === 0) {
    return (
      <div className="col">
        <div className="eyebrow">RESERVOIRS // CHONBURI PROVINCE</div>
        <div className="skeleton" style={{ height: 12, marginTop: 8 }} />
        <div className="skeleton" style={{ height: 12, marginTop: 6, width: "80%" }} />
      </div>
    );
  }
  if (!data.length) return null;

  // Dams that are draining come first: the one that runs out first is the
  // one the mayor has to act on.
  const rows = [...data].sort((a, b) => (a.daysRemaining ?? Infinity) - (b.daysRemaining ?? Infinity));
  const worst = rows[0];
  const worstLevel = alertLevel(worst.daysRemaining);
  // Which day's report this is. A daily report has no meaningful "age in
  // minutes" — it has a date, and the date is what the reader needs.
  const reportDate = reportLabel(rows[0]?.observedAt);

  // Province total, over conservation capacity — the same denominator the
  // per-row percentages use.
  const totalCurrent = rows.reduce((s, r) => s + (r.currentVolMCM ?? 0), 0);
  const totalMax     = rows.reduce((s, r) => s + (r.maxVolMCM ?? 0), 0);
  const totalPct     = totalMax > 0 ? Math.round((totalCurrent / totalMax) * 100) : null;

  return (
    <div className="col" style={{ gap: 6 }}>
      <PanelHeader
        title="RESERVOIRS // CHONBURI PROVINCE"
        source="RID"
        fallbackTier={fallbackTier}
        actions={reportDate ? (
          <span className="mono report-date" title="RID daily storage report — the date the report covers">
            {reportDate}
          </span>
        ) : undefined}
      />

      {/* The dam that runs dry first, if any does */}
      {(worstLevel === "critical" || worstLevel === "low") && (
        <div style={{
          border: `1px solid ${LEVEL_COLOR[worstLevel]}`,
          padding: "5px 8px",
          color: LEVEL_COLOR[worstLevel],
        }}>
          <div className="eyebrow mono">
            {worstLevel === "critical" ? "⚠ CRITICAL" : "⚠ LOW WATER"}
          </div>
          <div className="mono">
            {worst.name.replace("อ่างเก็บน้ำ","").trim()}
            {" — "}{worst.daysRemaining != null ? `${worst.daysRemaining}d of usable water` : "—"}
          </div>
        </div>
      )}

      {/* Province total */}
      <div>
        <div className="spread" style={{ alignItems: "center" }}>
          <div className="eyebrow">PROVINCE TOTAL</div>
          <div className="eyebrow mono" style={{ color: totalPct != null && totalPct < 30 ? "var(--warn)" : "var(--data)" }}>
            {totalPct != null ? `${totalPct}%` : "—"}
          </div>
        </div>
        <Bar pct={totalPct} level={totalPct != null && totalPct < 30 ? "low" : "ok"} />
        <div className="eyebrow mono" style={{ color: "var(--text-3)" }}>
          {totalCurrent.toFixed(1)} / {totalMax.toFixed(1)} MCM
        </div>
      </div>

      {/* Per-reservoir rows */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {rows.map((r) => {
          const level = alertLevel(r.daysRemaining);
          const col = LEVEL_COLOR[level];
          const shortName = r.name.replace("อ่างเก็บน้ำ","").trim();
          return (
            <div key={r.id} style={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <div className="spread" style={{ alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ color: col, fontFamily: "var(--font-mono)", fontSize: "var(--size-eyebrow)" }}>
                    {TREND_ARROW[r.trend]}
                  </span>
                  <span style={{ fontSize: "var(--size-eyebrow)" }}>{shortName}</span>
                </div>
                <div className="eyebrow mono" style={{ color: col }}>
                  {r.capacityPct != null ? `${r.capacityPct.toFixed(0)}%` : "—"}
                  {r.daysRemaining != null ? ` · ${r.daysRemaining}d` : ""}
                </div>
              </div>
              <Bar pct={r.capacityPct} level={level} />
            </div>
          );
        })}
      </div>

      <div className="eyebrow mono" style={{ color: "var(--text-3)" }}>
        ↑ filling · ↓ draining · days = usable water ÷ net daily depletion
      </div>
    </div>
  );
}
